import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Zoom,
} from "@mui/material";
import { HEADER_HEIGHT } from "../../utils/ui.constants";
import Options from "./components/Options";
import { useEffect, useMemo, useState } from "react";
import { LuFilter, LuPlusCircle } from "react-icons/lu";
import { asyncGetList } from "../../utils/httpRequests";
import { useSelector } from "react-redux";
import { useAlertContext } from "../../contexts/alert/AlertProvider";
import Tablepagination from "../../components/common/tableapp/TablePagination";
import TableApp from "../../components/common/tableapp/TableApp";
import { SortOrder, TableColumn } from "react-data-table-component";
import { CategoryType } from "../../types/category.type";
import SaveResult from "./components/SaveResult";
import { isEmpty } from "lodash";
import Actions from "./components/Actions";
import Search from "./components/Search";
import ExportExcel from "./components/ExportExcel";

interface ICategoryBase {
  category: CategoryType;
}

const heights = {
  header: HEADER_HEIGHT,
  py: "32px",
  topList: "50px",
  pagination: "42px",
};
const widthes = {
  filter: "350px",
};

function CategoryBase({ category }: ICategoryBase) {
  const { showAlert } = useAlertContext();
  const token = useSelector((state: any) => state.auth.token);
  const [load, setLoad] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [openModalForm, setOpenModalForm] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [clickedData, setClickedData] = useState<any>();
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [fastCondition, setFastCondition] = useState<any[]>([]);
  const [toggleClear, setToggleClear] = useState<boolean>(false);
  const [expandFilter, setExpandFilter] = useState<boolean>(false);
  const [sort, setSort] = useState<{ [key: string]: number }>({});
  const [data, setData] = useState<any[]>([]);
  const [fastTags, setFastTags] = useState<string[]>([]);
  const [fastFields, setFastFields] = useState<
    { label: string; value: string }[]
  >([]);
  const [condition, setCondition] = useState<
    { [key: string]: any } | undefined
  >({});
  const [paginationOption, setPaginationOption] = useState<{
    page: number;
    limit: number;
    totalRows: number;
  }>({
    limit: 20,
    page: 1,
    totalRows: 0,
  });

  // row per page change
  const handleRowPerPageChange = (value: number) => {
    setPaginationOption({ ...paginationOption, page: 1, limit: value });
  };

  const applyFastText = (fastTags: string[]) => {
    if (fastTags?.length > 0) {
      const fastCondition: any[] = [];
      for (let i = 0; i < fastTags.length; i++) {
        const word = fastTags[i];
        const block = {
          $or: (fastFields || []).map((item) => {
            return { [item.value]: { $regex: word, $options: "i" } };
          }),
        };
        fastCondition.push(block);
      }
      setFastCondition(fastCondition);
    } else {
      setFastCondition([]);
    }
    setLoad(load + 1);
    setExpandFilter(false);
  };

  const getListData = async () => {
    try {
      if (loading) return;
      setLoading(true);
      let query: { [key: string]: any } = {};
      let conditionToSearch: { [key: string]: any } = {
        page: paginationOption.page,
        limit: paginationOption.limit,
      };
      if (sort) {
        conditionToSearch.sort = sort;
      }
      if (fastCondition.length > 0) {
        query.$and = fastCondition;
      } else {
        query = { ...condition };
      }
      if (category.outerQuery) {
        conditionToSearch = { ...conditionToSearch, ...query };
      } else {
        conditionToSearch = { ...conditionToSearch, q: query };
      }

      const resp = await asyncGetList({
        apiCode: category.apiCode,
        token,
        stringifyData: category?.stringifyData,
        condition: conditionToSearch,
      });

      if (resp?.status === 200) {
        setData(resp?.data || []);
        setLoading(false);
        if (!category?.paginationText) {
          const respCount = await asyncGetList({
            apiCode: category.apiCode,
            token,
            condition: { count: 1, q: query },
          });
          if (respCount?.status === 200) {
            setPaginationOption({
              ...paginationOption,
              totalRows: respCount.data.rows_number || 0,
            });
          }
        }
      }
    } catch (error: any) {
      showAlert({
        type: "error",
        message:
          error?.data?.message || error?.data?.error || "Lỗi khi tải dữ liệu",
      });
    } finally {
      setFastCondition([]);
      setToggleClear(!toggleClear);
      setSelectedRows([]);
    }
  };

  const handleRowClicked = (row: any) => {
    if (category.Form) {
      setClickedData(row);
      setOpenModalForm(true);
      setIsEdit(true);
    }
  };

  const handleCloseModalForm = () => {
    setClickedData(null);
    setOpenModalForm(false);
    setIsEdit(false);
  };

  const handleSort = (
    selectedColumn: TableColumn<any>,
    sortDirection: SortOrder
  ) => {
    if (selectedColumn && selectedColumn.sortField) {
      setSort({ [selectedColumn.sortField]: sortDirection === "asc" ? 1 : -1 });
    }
  };

  const columns: TableColumn<any>[] = useMemo(() => {
    return (category.columns || []).map((col: TableColumn<any>) => {
      if (!col.cell) {
        return {
          ...col,
          cell: (row, index) => {
            return (
              <Tooltip
                placement="right"
                arrow
                TransitionComponent={Zoom}
                title={`${
                  col.format ? col.format(row, index) : col.selector?.(row)
                }`}
              >
                <div data-tag="allowRowEvents" className="two-lines-ellipsis">
                  {col.format ? col.format(row, index) : col.selector?.(row)}
                </div>
              </Tooltip>
            );
          },
        };
      }
      return col;
    });
  }, [category.columns]);

  useEffect(() => {
    if (category.apiCode) {
      getListData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationOption.limit, paginationOption.page, load, sort]);

  useEffect(() => {
    if (category.apiCode) {
      setCondition([]);
      setFastFields(category.fastFields || []);
      setPaginationOption({ limit: 20, page: 1, totalRows: 0 });
      setLoad(load + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category.apiCode]);

  useEffect(() => {
    setFastTags([]);
    setPaginationOption({ limit: 20, page: 1, totalRows: 0 });
    setLoad(load + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition]);

  return (
    <>
      {/* Form  */}
      {category?.Form && openModalForm && (
        <category.Form
          apiCode={category?.apiCode}
          isEdit={isEdit}
          defaultValues={clickedData}
          open={openModalForm}
          onClose={handleCloseModalForm}
          setLoad={setLoad}
        />
      )}
      {/* Form end */}
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          backgroundColor: "background.paper",
          borderRadius: "10px",
        }}
      >
        {/* Top */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            flexShrink: 0,
            height: heights.topList,
            px: 2,
            borderBottom: "1px dashed",
            borderColor: "divider",
          }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography
              sx={{ fontSize: "20px", lineHeight: "20px", fontWeight: 600 }}
            >
              {category?.title || "Danh sách"}
            </Typography>
          </Stack>

          {fastFields && fastFields.length > 0 ? (
            <Box sx={{ width: "35%" }}>
              <Search
                loading={loading}
                tags={fastTags}
                setTags={setFastTags}
                originFields={category.fastFields || []}
                fields={fastFields}
                setFields={setFastFields}
                handleSearch={applyFastText}
              />
            </Box>
          ) : (
            <>
              {!!category?.renderInnerTop &&
                category.renderInnerTop({ data: data })}
            </>
          )}
          <Stack direction="row" alignItems="center" gap={1}>
            {category.showActions && (
              <Box
                sx={{
                  visibility: selectedRows?.length > 0 ? "visible" : "hidden",
                }}
              >
                <Actions
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  setToggleClear={setToggleClear}
                  apiCode={category?.apiCode}
                  setLoad={setLoad}
                />
              </Box>
            )}
            {category?.allowCreate && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                sx={{
                  boxShadow: "none",
                  color: "common.white",
                  textTransform: "none",
                  "&:hover": { boxShadow: "none" },
                }}
                startIcon={<LuPlusCircle size={16} />}
                onClick={() => setOpenModalForm(true)}
              >
                Thêm
              </Button>
            )}
            {category.showExportExcel && (
              <ExportExcel
                setLoad={setLoad}
                category={category}
                filenameExport={category?.filenameExport}
                totalRows={paginationOption.totalRows}
              />
            )}
            {!isEmpty(condition) && !!paginationOption.totalRows && (
              <SaveResult
                condition={condition}
                apiCode={category?.apiCode}
                setLoad={setLoad}
                title={`Lưu ${paginationOption.totalRows} kết quả`}
              />
            )}
            <button
              hidden
              id="btn-reload-list"
              onClick={() => setLoad(load + 1)}
            >
              Reload
            </button>
            {category.showOptions && (
              <Options category={category} setLoad={setLoad} />
            )}
            {category.allowFilter && (
              <IconButton
                onClick={() => setExpandFilter(!expandFilter)}
                sx={{
                  borderRadius: "10px",
                  color: "common.white",
                  backgroundColor: "secondary.main",
                  "&:hover": {
                    backgroundColor: "secondary.main",
                  },
                }}
              >
                <LuFilter size={16} />
              </IconButton>
            )}
          </Stack>
        </Stack>
        {/* Top end */}
        {/* Table */}
        <Box
          id="table-filter-layout"
          sx={{
            width: "100%",
            height: `calc(100vh - ${heights.header} - ${heights.py} - ${heights.topList} - ${heights.pagination})`,
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              width: category.allowFilter
                ? `calc(100%${expandFilter ? ` - ${widthes.filter}` : ""})`
                : "100%",
              height: "100%",
              transition: "all 0.1s",
              transitionTimingFunction: "ease-out",
            }}
          >
            <TableApp
              toggleCleared={toggleClear}
              rowPerPage={paginationOption.limit}
              columns={columns}
              data={data}
              selectableRows={category.selectableRows}
              fixedHeader
              sortServer={category?.sortServer}
              onSort={handleSort}
              progressPending={loading}
              onRowClicked={!category?.Expand ? handleRowClicked : undefined}
              expandableRows={!!category?.Expand}
              expandOnRowClicked={!!category?.Expand}
              expandableRowsHideExpander
              expandableRowsComponent={
                category.Expand
                  ? ({ data }) => (
                      <>
                        {category?.Expand && (
                          <category.Expand
                            data={data}
                            onOpenEdit={() => handleRowClicked(data)}
                            setLoad={setLoad}
                          />
                        )}
                      </>
                    )
                  : undefined
              }
              onSelectedRowsChange={setSelectedRows}
              fixedHeaderScrollHeight={`calc(100vh - ${HEADER_HEIGHT} - ${heights.py} - ${heights.topList} - ${heights.pagination})`}
            />
          </Box>
          {/* Filter */}
          {category.allowFilter && (
            <Box
              sx={{
                height: "100%",
                width: expandFilter ? widthes.filter : "0px",
                transition: "all 0.1s",
                transitionTimingFunction: "ease-out",
                borderLeft: "2px solid",
                borderColor: expandFilter ? "primary.main" : "transparent",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                }}
              >
                {category.Filter && (
                  <category.Filter
                    loading={loading}
                    setCondition={setCondition}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
        {/* Table end */}
        {/* Pagination */}
        <Box
          sx={{
            mt: "auto",
            borderTop: "1px dashed",
            borderColor: "divider",
            height: heights.pagination,
          }}
        >
          {!loading && (
            <>
              {!category?.paginationText ? (
                <Tablepagination
                  currentPage={paginationOption.page}
                  onChangePage={(value) =>
                    setPaginationOption({ ...paginationOption, page: value })
                  }
                  rowsPerPage={paginationOption.limit}
                  rowCount={paginationOption.totalRows}
                  onChangeRowsPerPage={handleRowPerPageChange}
                  paginationRowsPerPageOptions={[20, 50, 100, 500]}
                />
              ) : (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ width: "100%", height: "100%" }}
                >
                  <Typography>Tổng số dòng: {data?.length}</Typography>
                </Stack>
              )}
            </>
          )}
        </Box>
        {/* Pagination end */}
      </Box>
    </>
  );
}

export default CategoryBase;
